import React, { useId } from "react";
import { useGuestProfileLanguage } from "./guestProfileI18n";

const SECTIONS = [
  ["Company details", [
    ["name", "Company name"], ["legal_name", "Legal name"],
    ["tax_id", "Tax / VAT ID"], ["registration_number", "Registration number"],
    ["email", "Email", "email"], ["phone", "Phone", "tel"],
    ["website", "Website"], ["industry", "Industry"], ["company_type", "Company type"],
  ]],
  ["Company address", [
    ["address", "Address"], ["city", "City"], ["region", "Region / State"],
    ["postal_code", "Postal code"], ["country", "Country"],
  ]],
  ["Billing details", [
    ["billing_email", "Billing email", "email"], ["billing_address", "Billing address"],
    ["billing_city", "Billing city"], ["billing_region", "Billing region / State"],
    ["billing_postal_code", "Billing postal code"], ["billing_country", "Billing country"],
  ]],
];

const FIELD_LIMITS = {
  tax_id: 120, registration_number: 120, phone: 120, company_type: 120,
  industry: 160, postal_code: 40, billing_postal_code: 40, address: 500, billing_address: 500,
};

export const EMPTY_BOOKER_COMPANY = Object.fromEntries([
  ...SECTIONS.flatMap(([, fields]) => fields.map(([name]) => [name, ""])),
  ["status", "active"], ["notes", ""],
]);

export function companyBookerContactRequired(settings) {
  const value = settings?.require_company_booker_contact;
  return ![false, 0, "false", "0"].includes(value);
}

export function BookerTypeTabs({ value, onChange }) {
  const id = useId();
  const { t } = useGuestProfileLanguage();
  return (
    <div className="booker-customer-types" role="tablist" aria-label={t("Booker customer type")}>
      {[["person", "Person"], ["company", "Company"]].map(([type, label]) => (
        <button key={type} id={`${id}-${type}`} type="button" role="tab"
          aria-selected={value === type} tabIndex={value === type ? 0 : -1} onClick={() => onChange(type)}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const nextType = event.key === "Home" ? "person" : event.key === "End" ? "company" : type === "person" ? "company" : "person";
            onChange(nextType);
            document.getElementById(`${id}-${nextType}`)?.focus();
          }}
          className={`booker-customer-types__tab${value === type ? " is-active" : ""}`}>
          {t(label)}
        </button>
      ))}
    </div>
  );
}

export function BookerCompanyContact({ value, onChange, required }) {
  const id = useId();
  const { t } = useGuestProfileLanguage();
  return (
    <section className="booker-company-section booker-company-contact">
      <div>
        <h3>{t("Reservation contact")}</h3>
        <p>{t("The person who called or contacted you to book on behalf of the company. This contact belongs to this reservation group and is not assigned as a guest.")}</p>
      </div>
      <label className="booker-company-field" htmlFor={id}>
        <span>{t("Contact person full name")}{required ? " *" : t(" (optional)")}</span>
        <input id={id} name="booker_contact_name" value={value} onChange={onChange}
          required={required} maxLength={255} autoComplete="off" placeholder={t("First and last name")} />
      </label>
    </section>
  );
}

export default function BookerCompanyForm({ formData, onChange, contactName, onContactChange, contactRequired }) {
  const id = useId();
  const { t } = useGuestProfileLanguage();
  return (
    <div className="booker-company-form">
      <BookerCompanyContact value={contactName} onChange={onContactChange} required={contactRequired} />
      {SECTIONS.map(([title, fields]) => (
        <section className="booker-company-section" key={t(title)}>
          <h3>{t(title)}</h3>
          <div className="booker-company-grid">
            {fields.map(([name, label, type = "text"]) => (
              <label className="booker-company-field" htmlFor={`${id}-${name}`} key={name}>
                <span>{t(label)}{name === "name" ? " *" : ""}</span>
                <input id={`${id}-${name}`} name={name} type={type}
                  value={formData[name] ?? ""} onChange={onChange} required={name === "name"}
                  maxLength={FIELD_LIMITS[name] || 255} />
              </label>
            ))}
          </div>
        </section>
      ))}
      <section className="booker-company-section">
        <h3>{t("Additional information")}</h3>
        <div className="booker-company-grid">
          <label className="booker-company-field" htmlFor={`${id}-status`}>
            <span>{t("Status")}</span>
            <select id={`${id}-status`} name="status" value={formData.status || "active"} onChange={onChange}>
              <option value="active">{t("Active")}</option>
              <option value="inactive">{t("Inactive")}</option>
            </select>
          </label>
          <label className="booker-company-field booker-company-field--wide" htmlFor={`${id}-notes`}>
            <span>{t("Notes")}</span>
            <textarea id={`${id}-notes`} name="notes" value={formData.notes ?? ""} onChange={onChange} rows={3} maxLength={20000} />
          </label>
        </div>
      </section>
    </div>
  );
}
